import { AdjustmentType } from '@/enums/adjustment-type';
import { PromotionalContentPlacement } from '@/enums/promotional-content-placement';
import { MovieCatalogRequest } from '@/types/movie-catalog';
import { PagedRequest } from '@/types/paged-requests/paged-request';
import { PromoContentConfirmationPageRelationshipType, PromoContentRelationshipType } from '@cinemark/content-api-client';
import { PlanType } from '@cinemark/profile-api-client';

export const QueryKeys = {
  // Global keys that persist across sessions
  global: {
    allTheaters: ['allTheaters'] as const,
    analyticsLog: ['analyticsLog'] as const,
    analyticsQueue: ['analyticsQueue'] as const,
    analyticsReady: ['analyticsReady'] as const,
    articles: (pagedRequest?: PagedRequest) => ['articles', pagedRequest] as const,
    attributes: (attributeRelationship?: string) =>
      ['attributes', attributeRelationship] as const,
    concessionFulfillmentFees: (theaterId: number) => ['concessionFulfillmentFees', theaterId] as const,
    concessionsMenu: (theaterId: number, eventDate: string, timeZone?: string, isFamilyEntertainmentCenter?: boolean) =>
      ['concessionsMenu', theaterId, eventDate, timeZone, isFamilyEntertainmentCenter] as const,
    configurations: ['configurations'] as const,
    featureFlags: ['feature-flags'] as const,
    guestPaymentMethods: (theaterId: number, isMobile: boolean) => ['guestPaymentMethods', theaterId, isMobile] as const,
    layout: ['layout'] as const,
    loyaltySubscriptionPlanByPlanType: (theaterId: number, planType: PlanType) => ['loyaltySubscriptionPlan', theaterId, planType] as const,
    media: (mediaId: string) => ['media', mediaId] as const,
    movie: (movieId: string, depth?: number): readonly ['movie', string] | readonly ['movie', string, number] =>
      depth === undefined ? ['movie', movieId] : ['movie', movieId, depth],
    movieCatalog: (request: MovieCatalogRequest) => ['movieCatalog', request] as const,
    movieGlobal: (depth?: number): readonly ['movieGlobal'] | readonly ['movieGlobal', number] =>
      depth === undefined ? ['movieGlobal'] : ['movieGlobal', depth],
    movieIdsByTheater: (theaterId: number) => ['movieIdsByTheater', theaterId] as const,
    movieNews: ['movieNews'] as const,
    moviesByMovieIds: (cinemarkMovieIds: number[]) => ['moviesByMovieIds', cinemarkMovieIds] as const,
    moviesHeroScroller: (theaterId: number, timeZone?: string) => ['moviesHeroScroller', theaterId, timeZone] as const,
    movieShowtimeDates: (movieId: number | undefined, zipCode?: string) => ['movieShowtimeDates', movieId, zipCode] as const,
    movieShowtimes: (movieId: number | undefined, date?: string, zipCode?: string, pageSize?: number) =>
      ['movieShowtimes', movieId, date, zipCode, pageSize] as const,
    nonCashTicketsByAdjustmentType: (theaterId: number, adjustmentType: AdjustmentType) => ['nonCashTickets', theaterId, adjustmentType] as const,
    policyPages: ['policyPages'] as const,
    privateWatchPartyContent: ['privateWatchPartyContent'] as const,
    promoContentByArea: (
      relationType: PromoContentRelationshipType,
      placement: PromotionalContentPlacement,
      pageSize?: number
    ) => ['promoContentByArea', relationType, placement, pageSize] as const,
    promoContentByRelationId: (
      payloadId: string,
      placement: PromotionalContentPlacement,
      confPageRelationType?: PromoContentConfirmationPageRelationshipType,
      pageSize?: number
    ) => ['promoContentByRelationId', payloadId, placement, confPageRelationType, pageSize] as const,
    states: ['states'] as const,
    theaterById: (theaterId: number) => ['theaterById', theaterId] as const,
    theaterContext: ['theaterContext'] as const,
    theaterOperatingHours: (theaterId: string, businessStartDate: string, businessEndDate: string) =>
      ['theaterOperatingHours', theaterId, businessStartDate, businessEndDate] as const,
    theatersByGeoLocation: (
      latitude?: number,
      longitude?: number,
      theaterIds?: number[],
      radius?: number
    ) => ['theatersByGeoLocation', latitude, longitude, theaterIds, radius] as const,
    theatersByTheaterIds: (theaterIds: number[]) =>
      ['theatersByTheaterIds', theaterIds] as const,
    theatersByZipCode: (zipCode: string, theaterIds?: number[], radius?: number) =>
      ['theatersByZipCode', zipCode, theaterIds, radius] as const,
    theatersContext: ['theatersContext'] as const,
    theaterSettings: (theaterId: number) => ['theaterSettings', theaterId] as const,
    theaterShowtimeDates: (theaterId: string, timeZone?: string) => ['theaterShowtimeDates', theaterId, timeZone] as const,
    theaterShowtimes: (
      theaterId: string,
      date?: string,
      movieIds?: number[],
      timeZone?: string,
      isAuthenticated?: boolean
    ) => ['theaterShowtimes', theaterId, date, movieIds, timeZone, isAuthenticated] as const,
    ticketFees: (theaterId: number) => ['ticketFees', theaterId] as const,
    ticketPrices: (theaterId: number, showtimeId: number) =>
      ['ticketPrices', theaterId, showtimeId] as const,
    ticketSeatMapContent: ['ticketSeatMapContent'] as const,
    ticketSeatMapUnavailableSeats: (theaterId: number, showtimeId: number, linkedShowtimeId?: number) =>
      ['ticketSeatMapUnavailableSeats', theaterId, showtimeId, linkedShowtimeId] as const,
    zipByGeo: (latitude: number, longitude: number) => ['zipByGeo', latitude, longitude] as const,
  },
  // User-specific keys that should be cleared on logout/auth changes
  user: {
    analytics: ['analytics'] as const,
    emailSubscriptions: ['emailSubscriptions'] as const,
    favoriteTheaters: ['favoriteTheaters'] as const,
    giftCards: ['giftCards'] as const,
    movieClubPayment: ['movieClubPayment'] as const,
    movieClubPriceChange: ['movieClubPriceChange'] as const,
    paymentMethods: ['paymentMethods'] as const,
    paymentMethodsResponse: (hasSubscriptionItems = false) =>
      ['paymentMethodsResponse', hasSubscriptionItems] as const,
    profile: ['profile'] as const,
    sharedState: ['shared-state'] as const,
    shippingAddresses: ['shippingAddresses'] as const,
    shoppingCart: ['shoppingCart'] as const,
    shoppingCartReview: ['shoppingCartReview'] as const,
    storeCredits: ['storeCredits'] as const,
    transactions: ['transactions'] as const,
    twoFactorAuth: ['twoFactorAuth'] as const,
    twoFactorDevices: ['twoFactorDevices'] as const,
    watchList: ['watchList'] as const,
    watchListSimple: ['watchList', 'simple'] as const,
  },
};
